/**
 * StatsCards.jsx
 * Premium stat tiles — neutral background, colored icon, animated number.
 * Stripe/Linear inspired.
 */
import React, { useEffect, useRef } from "react";
import { DollarSign, CheckCircle, Clock, Home } from "lucide-react";

// ─── Animated counter ─────────────────────────────────────────────────────────
function Counter({ value, prefix = "", suffix = "" }) {
  const ref    = useRef(null);
  const numVal = parseFloat(String(value).replace(/[^0-9.]/g,"")) || 0;

  useEffect(() => {
    if (!ref.current) return;
    const duration  = 800;
    const startTime = performance.now();
    const isLarge   = numVal > 999;

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const ease     = 1 - Math.pow(1 - progress, 3);
      const current  = Math.round(numVal * ease);
      if (ref.current) {
        ref.current.textContent =
          prefix + (isLarge ? current.toLocaleString("en-PK") : current) + suffix;
      }
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [numVal, prefix, suffix]);

  return <span ref={ref}>{prefix}{numVal.toLocaleString("en-PK")}{suffix}</span>;
}

// ─── Mini sparkline bar chart ─────────────────────────────────────────────────
function SparkBars({ values = [], color }) {
  const max = Math.max(...values, 1);
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:32 }}>
      {values.map((v, i) => (
        <div key={i} style={{
          width: 6, borderRadius:3,
          height: `${Math.max((v/max)*100, 8)}%`,
          background: color,
          opacity: i === values.length-1 ? 1 : 0.35,
          transition:"height 0.3s ease",
        }} />
      ))}
    </div>
  );
}

// ─── Build last-7-day booking counts ─────────────────────────────────────────
function getLast7Days(bookings) {
  const counts = Array(7).fill(0);
  const now    = new Date();
  bookings.forEach(b => {
    const diff = Math.floor((now - new Date(b.createdAt || b.checkIn)) / 86400000);
    if (diff >= 0 && diff < 7) counts[6 - diff]++;
  });
  return counts;
}

export default function StatsCards({ bookings = [] }) {
  const totalRevenue = bookings.reduce((s,b) => s+(b.totalPrice||0), 0);
  const totalCollect = bookings.reduce((s,b) => s+(b.advancePaid||0), 0);
  const totalPending = bookings.reduce((s,b) => s+(b.remaining||0), 0);
  const activeNow    = bookings.filter(b=>b.status==="Active").length;
  const upcoming     = bookings.filter(b=>b.status==="Upcoming").length;
  const sparkData    = getLast7Days(bookings);
  const pct          = totalRevenue > 0 ? Math.round((totalCollect/totalRevenue)*100) : 0;

  const cards = [
    {
      label:   "Total Revenue",
      sub:     `${bookings.length} total bookings`,
      value:   Math.round(totalRevenue),
      prefix:  "PKR ",
      icon:    DollarSign,
      color:   "var(--gold)",
      iconBg:  "var(--gold-dim)",
      spark:   sparkData,
    },
    {
      label:   "Collected",
      sub:     `${pct}% of total revenue`,
      value:   Math.round(totalCollect),
      prefix:  "PKR ",
      icon:    CheckCircle,
      color:   "var(--success)",
      iconBg:  "var(--success-dim)",
      spark:   sparkData.map(v => Math.round(v * 0.7)),
    },
    {
      label:   "Outstanding",
      sub:     totalPending > 0 ? "Pending collection" : "All cleared!",
      value:   Math.round(totalPending),
      prefix:  "PKR ",
      icon:    Clock,
      color:   totalPending > 0 ? "var(--danger)" : "var(--success)",
      iconBg:  totalPending > 0 ? "var(--danger-dim)" : "var(--success-dim)",
      spark:   sparkData.map(v => Math.round(v * 0.3)),
    },
    {
      label:   "Active Now",
      sub:     `${upcoming} upcoming`,
      value:   activeNow,
      prefix:  "",
      icon:    Home,
      color:   "var(--info)",
      iconBg:  "var(--info-dim)",
      spark:   sparkData,
    },
  ];

  return (
    <div style={{
      display:"grid",
      gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",
      gap:16,
      marginBottom:28,
    }}>
      {cards.map((card, i) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="stat-card" style={{ animationDelay:`${i*0.06}s` }}>
            {/* Icon */}
            <div className="stat-icon" style={{ background: card.iconBg }}>
              <Icon size={18} color={card.color} strokeWidth={2} />
            </div>

            {/* Number */}
            <div className="stat-number">
              <Counter value={card.value} prefix={card.prefix} />
            </div>

            {/* Label */}
            <div className="stat-label">{card.label}</div>
            <div className="stat-sub">{card.sub}</div>

            {/* Sparkline */}
            <div style={{
              position:"absolute", bottom:16, right:20,
              opacity:0.6,
            }}>
              <SparkBars values={card.spark} color={card.color} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
